import { type ReactNode, useEffect, useState, type JSX } from "react";

import { readerSession } from "./connect.js";
import { ConnectionLayout, ConnectionRetry } from "./ConnectionLayout.js";
import { readerErrorMessage } from "./errors.js";

type RecoveryState =
  | { readonly status: "loading" }
  | { readonly status: "ready" }
  | { readonly status: "error"; readonly message: string };

export function PendingMutationRecovery({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const [attempt, setAttempt] = useState(0);
  const [recovery, setRecovery] = useState<RecoveryState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    void readerSession
      .recoverPendingMutations({ signal: controller.signal, timeoutMs: 120_000 })
      .then((outcomes) => {
        if (controller.signal.aborted) {
          return;
        }
        const problem = outcomes.flatMap((outcome) => (outcome.ok ? [] : [outcome.problem]))[0];
        setRecovery(problem ? { status: "error", message: problem.message } : { status: "ready" });
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setRecovery({
            status: "error",
            message: readerErrorMessage(reason, "Reader could not recover an interrupted write."),
          });
        }
      });
    return () => controller.abort();
  }, [attempt]);

  if (recovery.status === "loading") {
    return <ConnectionLayout status="Recovering interrupted writes…" />;
  }
  if (recovery.status === "error") {
    return (
      <ConnectionLayout
        status="Reader paused before loading the collection."
        error={recovery.message}
      >
        <ConnectionRetry
          error={recovery.message}
          onRetry={() => {
            setRecovery({ status: "loading" });
            setAttempt((current) => current + 1);
          }}
        />
      </ConnectionLayout>
    );
  }
  return <>{children}</>;
}
